import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, Image as ImageIcon, Files, Trash2, CheckCircle2 } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

const IMAGE_CONFIGS = {
  'pdf-to-png': {
    title: 'PDF to PNG',
    format: 'png',
    mimeType: 'image/png',
    color: 'emerald',
    description: 'Convert PDF pages to high-quality PNG images with transparency support. Supports batch conversion!'
  },
  'pdf-to-jpg': {
    title: 'PDF to JPG',
    format: 'jpg',
    mimeType: 'image/jpeg',
    color: 'rose',
    description: 'Convert PDF pages to compressed JPG images for smaller file sizes. Supports batch conversion!'
  }
};

const colorClasses = {
  emerald: { bg: 'from-emerald-500 to-green-600', light: 'from-emerald-50 to-green-50', text: 'text-emerald-500' },
  rose: { bg: 'from-rose-500 to-red-600', light: 'from-rose-50 to-red-50', text: 'text-rose-500' },
};

function PdfToImageTool({ configKey }) {
  const config = IMAGE_CONFIGS[configKey];
  const colors = colorClasses[config.color];
  
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [convertedFiles, setConvertedFiles] = useState([]);
  const [scale, setScale] = useState("2");
  const [quality, setQuality] = useState("0.9");
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = `${config.title} - Convert PDF to ${config.format.toUpperCase()} Images - KayTool`;
  }, [config]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const pdfFiles = selectedFiles.filter(f => f.type === 'application/pdf');
    
    if (pdfFiles.length === 0) {
      toast.error("Please select valid PDF files");
      return;
    }
    
    setFiles(prev => [...prev, ...pdfFiles.map(f => ({ file: f, status: 'pending', images: [] }))]);
    setConvertedFiles([]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const pdfFiles = droppedFiles.filter(f => f.type === 'application/pdf');
    
    if (pdfFiles.length === 0) {
      toast.error("Please drop valid PDF files");
      return;
    }
    
    setFiles(prev => [...prev, ...pdfFiles.map(f => ({ file: f, status: 'pending', images: [] }))]);
    setConvertedFiles([]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setConvertedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFiles([]);
    setConvertedFiles([]);
    setCurrentFileIndex(0);
  };

  const convertSinglePdf = async (fileObj) => {
    const arrayBuffer = await fileObj.file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pageImages = [];
    const baseName = fileObj.file.name.replace('.pdf', '');
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: parseFloat(scale) });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      // White background for JPG
      if (config.format === 'jpg') {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      await page.render({ canvasContext: context, viewport }).promise;
      
      const dataUrl = canvas.toDataURL(config.mimeType, parseFloat(quality));
      const blob = await fetch(dataUrl).then(r => r.blob());
      
      pageImages.push({
        pageNum: i,
        dataUrl,
        blob,
        name: pdf.numPages > 1 
          ? `${baseName}_page_${i}.${config.format}`
          : `${baseName}.${config.format}`,
        width: viewport.width,
        height: viewport.height
      });
    }
    
    return { images: pageImages, pdfName: baseName, totalPages: pdf.numPages };
  };

  const convertAllPdfs = async () => {
    if (files.length === 0) return;
    
    setIsProcessing(true);
    setConvertedFiles([]);
    
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      setCurrentFileIndex(i);
      
      setFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'processing' } : f
      ));
      
      try {
        const result = await convertSinglePdf(files[i]);
        results.push(result);
        
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'done', images: result.images } : f
        ));
      } catch (error) {
        console.error(`Error converting ${files[i].file.name}:`, error);
        
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error', errorMsg: error.message } : f
        ));
        
        results.push({ error: error.message, pdfName: files[i].file.name });
      }
    }
    
    setConvertedFiles(results);
    setIsProcessing(false);
    
    const successCount = results.filter(r => !r.error).length;
    const totalImages = results.filter(r => !r.error).reduce((sum, r) => sum + r.images.length, 0);
    
    if (successCount === files.length) {
      toast.success(`Converted ${totalImages} images from ${successCount} PDFs!`);
    } else {
      toast.warning(`${successCount} PDFs converted, ${files.length - successCount} failed`);
    }
  };

  const downloadAllAsZip = async () => {
    const successfulFiles = convertedFiles.filter(f => !f.error);
    if (successfulFiles.length === 0) {
      toast.error("No files to download");
      return;
    }
    
    // If only 1 PDF with 1 page, download directly
    if (successfulFiles.length === 1 && successfulFiles[0].images.length === 1) {
      const img = successfulFiles[0].images[0];
      saveAs(img.blob, img.name);
      toast.success("Image downloaded!");
      return;
    }
    
    const zip = new JSZip();
    
    for (const pdfResult of successfulFiles) {
      if (successfulFiles.length > 1 && pdfResult.images.length > 1) {
        // Multiple PDFs with multiple pages - create folders
        const folder = zip.folder(pdfResult.pdfName);
        for (const img of pdfResult.images) {
          folder.file(img.name, img.blob);
        }
      } else {
        // Single PDF or single pages - flat structure
        for (const img of pdfResult.images) {
          zip.file(img.name, img.blob);
        }
      }
    }
    
    const content = await zip.generateAsync({ type: 'blob' });
    const totalImages = successfulFiles.reduce((sum, f) => sum + f.images.length, 0);
    saveAs(content, `pdf_to_${config.format}_${totalImages}_images.zip`);
    toast.success(`Downloaded ${totalImages} images as ZIP!`);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case 'done':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'error':
        return <span className="w-4 h-4 text-red-500">✗</span>;
      default:
        return <FileText className="w-4 h-4 text-slate-400" />;
    }
  };

  const overallProgress = files.length > 0 
    ? Math.round((files.filter(f => f.status === 'done' || f.status === 'error').length / files.length) * 100)
    : 0;

  const totalImages = convertedFiles.filter(f => !f.error).reduce((sum, f) => sum + f.images.length, 0);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : `bg-gradient-to-br ${colors.light}`}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors.bg} flex items-center justify-center`}>
              <ImageIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${colors.bg} text-white rounded-full mb-4`}>
            <ImageIcon className="w-5 h-5" />
            <span className="font-medium">{config.title}</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Convert PDF to {config.format.toUpperCase()} Images
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            {config.description}
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Upload Area */}
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
              isDragging ? `border-${config.color}-500 bg-${config.color}-50` : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className={`w-12 h-12 mx-auto mb-4 ${colors.text}`} />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDFs or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Convert multiple PDF files to {config.format.toUpperCase()} images
                </p>
                <Badge variant="secondary" className="mt-3">
                  <Files className="w-3 h-3 mr-1" /> Batch conversion supported
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Settings & File List */}
          {files.length > 0 && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4">
                {/* Header with settings */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <h3 className="font-semibold dark:text-white flex items-center gap-2">
                    <Files className={`w-5 h-5 ${colors.text}`} />
                    {files.length} PDF{files.length > 1 ? 's' : ''} selected
                  </h3>
                  
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">Scale:</Label>
                      <Select value={scale} onValueChange={setScale} disabled={isProcessing}>
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1x</SelectItem>
                          <SelectItem value="2">2x</SelectItem>
                          <SelectItem value="3">3x</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {config.format === 'jpg' && (
                      <div className="flex items-center gap-2">
                        <Label className="text-sm whitespace-nowrap">Quality:</Label>
                        <Select value={quality} onValueChange={setQuality} disabled={isProcessing}>
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0.7">70%</SelectItem>
                            <SelectItem value="0.8">80%</SelectItem>
                            <SelectItem value="0.9">90%</SelectItem>
                            <SelectItem value="1">100%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <Button variant="ghost" size="sm" onClick={clearAll} className="text-red-500">
                      <Trash2 className="w-4 h-4 mr-1" /> Clear
                    </Button>
                  </div>
                </div>
                
                {/* Progress */}
                {isProcessing && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-600 dark:text-slate-300">
                        Converting {currentFileIndex + 1} of {files.length}...
                      </span>
                      <span className="text-slate-500">{overallProgress}%</span>
                    </div>
                    <Progress value={overallProgress} className="h-2" />
                  </div>
                )}
                
                {/* File List */}
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {files.map((fileObj, index) => (
                    <div 
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        fileObj.status === 'processing' ? 'bg-blue-50 dark:bg-blue-900/20' :
                        fileObj.status === 'done' ? 'bg-green-50 dark:bg-green-900/20' :
                        fileObj.status === 'error' ? 'bg-red-50 dark:bg-red-900/20' :
                        'bg-slate-50 dark:bg-slate-700/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {getStatusIcon(fileObj.status)}
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate dark:text-white">
                            {fileObj.file.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatSize(fileObj.file.size)}
                            {fileObj.images?.length > 0 && (
                              <span className="ml-2 text-green-600">
                                → {fileObj.images.length} image{fileObj.images.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      {!isProcessing && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => removeFile(index)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Convert Button */}
          {files.length > 0 && !isProcessing && convertedFiles.length === 0 && (
            <div className="text-center">
              <Button 
                onClick={convertAllPdfs}
                size="lg"
                className={`bg-gradient-to-r ${colors.bg}`}
              >
                <ImageIcon className="w-5 h-5 mr-2" />
                Convert {files.length} PDF{files.length > 1 ? 's' : ''} to {config.format.toUpperCase()}
              </Button>
            </div>
          )}

          {isProcessing && files.length > 0 && (
            <div className="text-center py-4">
              <Loader2 className={`w-8 h-8 mx-auto animate-spin ${colors.text}`} />
              <p className="mt-2 text-slate-600 dark:text-slate-300">Converting pages...</p>
            </div>
          )}

          {/* Success - Download */}
          {totalImages > 0 && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-semibold text-slate-800 dark:text-white mb-2">
                  Conversion Complete!
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  {totalImages} {config.format.toUpperCase()} image{totalImages > 1 ? 's' : ''} from {convertedFiles.filter(f => !f.error).length} PDF{convertedFiles.filter(f => !f.error).length > 1 ? 's' : ''}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={downloadAllAsZip} className={`bg-gradient-to-r ${colors.bg}`}>
                    <Download className="w-4 h-4 mr-2" />
                    {totalImages > 1 ? 'Download All (ZIP)' : `Download ${config.format.toUpperCase()}`}
                  </Button>
                  <Button variant="outline" onClick={clearAll}>
                    Convert More
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Image Preview Grid */}
          {totalImages > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold dark:text-white">
                {totalImages} {config.format.toUpperCase()} Images
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {convertedFiles.filter(f => !f.error).flatMap(pdfResult => 
                  pdfResult.images.map((img, imgIndex) => (
                    <Card key={`${pdfResult.pdfName}-${imgIndex}`} className="overflow-hidden dark:bg-slate-800">
                      <div className="bg-slate-100 dark:bg-slate-700 p-2 aspect-[3/4] flex items-center justify-center">
                        <img
                          src={img.dataUrl}
                          alt={img.name}
                          className="max-w-full max-h-full object-contain rounded"
                        />
                      </div>
                      <div className="p-2">
                        <p className="text-xs text-slate-600 dark:text-slate-400 truncate" title={img.name}>
                          {img.name}
                        </p>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export function PdfToPng() { return <PdfToImageTool configKey="pdf-to-png" />; }
export function PdfToJpg() { return <PdfToImageTool configKey="pdf-to-jpg" />; }
